import { Component, Input, Output, EventEmitter, OnChanges } from '@angular/core';

@Component({
  selector: 'app-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  standalone: false,
})
export class PaginationComponent implements OnChanges {
  @Input() currentPage: number = 1;
  @Input() totalCount: number = 0;
  @Input() pageSize: number = 10;
  @Input() hasNextPage: boolean = false;
  @Input() hasPreviousPage: boolean = false;

  @Output() pageChange = new EventEmitter<number>();

  totalPages: number = 1;
  pages: (number | '...')[] = [];

  ngOnChanges(): void {
    this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.pageSize));
    this.pages = this.buildPageRange();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.pageChange.emit(page);
    }
  }

  onPrev(): void {
    if (this.currentPage > 1) {
      this.pageChange.emit(this.currentPage - 1);
    }
  }

  onNext(): void {
    if (this.currentPage < this.totalPages) {
      this.pageChange.emit(this.currentPage + 1);
    }
  }

  get startCount(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endCount(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalCount);
  }

  /**
   * Builds a page range like: [1, 2, 3, '...', 10]
   * Always shows first, last, and up to 2 pages around current.
   */
  private buildPageRange(): (number | '...')[] {
    const total = this.totalPages;
    const current = this.currentPage;

    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: (number | '...')[] = [];
    const rangeStart = Math.max(2, current - 1);
    const rangeEnd = Math.min(total - 1, current + 1);

    // Always show page 1
    pages.push(1);

    // Ellipsis after page 1 if gap
    if (rangeStart > 2) {
      pages.push('...');
    }

    // Middle range
    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(i);
    }

    // Ellipsis before last page if gap
    if (rangeEnd < total - 1) {
      pages.push('...');
    }

    // Always show last page
    pages.push(total);

    return pages;
  }
}
